import React, { useState } from 'react';
import { Shield, Mail, Lock, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import api from '../services/api';

const SuperAdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await api.post('/login', { email, password });
            const { token, user } = response.data;

            if (user.role !== 'superadmin') {
                setError('Acesso Negado: Este usuário não possui privilégios de Super Administrador Global do Sistema.');
                setLoading(false);
                return;
            }

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            // Hard redirect to superadmin route
            window.location.href = '/superadmin';

        } catch (err) {
            console.error("Erro no login do Super Admin:", err);
            if (err.response) {
                setError(err.response.data.error || 'Credenciais inválidas.');
            } else if (err.request) {
                setError('Não foi possível conectar ao servidor. Verifique se ele está rodando.');
            } else {
                setError('Erro desconhecido. Tente novamente.');
            }
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4 font-sans relative overflow-hidden">
            {/* Elementos de Fundo Decorativos */}
            <div className="absolute top-0 w-full h-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600"></div>
            <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-[100px] opacity-20 animate-blob"></div>
            <div className="absolute bottom-[-20%] right-[-10%] w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-[100px] opacity-20 animate-blob animation-delay-2000"></div>

            <div className="bg-gray-800 p-8 rounded-2xl shadow-2xl w-full max-w-md relative z-10 border border-gray-700">
                <div className="text-center mb-8">
                    <div className="bg-gray-900 border border-gray-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg relative">
                        <Shield className="w-10 h-10 text-blue-500 z-10" />
                        <div className="absolute inset-0 bg-blue-500/20 rounded-2xl animate-pulse"></div>
                    </div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">Super Admin</h1>
                    <p className="text-gray-400 text-sm mt-2">Acesso Restrito ao Sistema</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-900/50 border-l-4 border-red-500 text-red-200 rounded-r-lg flex items-start text-sm animate-in fade-in slide-in-from-top-2">
                        <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleLogin} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2 ml-1">E-mail do Administrador</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-xl leading-5 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all sm:text-sm"
                                placeholder="admin@sistema.com"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2 ml-1">Senha de Acesso</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-xl leading-5 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all sm:text-sm"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:-translate-y-0.5 active:translate-y-0 mt-8"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                                Validando Acesso...
                            </>
                        ) : (
                            <>
                                Acessar Painel <ArrowRight className="ml-2 w-5 h-5" />
                            </>
                        )}
                    </button>

                    <div className="mt-6 text-center">
                        <a href="/" className="text-sm font-medium text-gray-500 hover:text-gray-300 transition-colors">
                            &larr; Voltar para o Sistema
                        </a>
                    </div>
                </form>
            </div>

            <div className="absolute bottom-4 text-center w-full text-xs text-gray-600 z-0">
                Acesso Monitorado &copy; {new Date().getFullYear()} WA-Desk
            </div>
        </div>
    );
};

export default SuperAdminLogin;
